import { CONFIG } from 'src/environments/environment';

import type { RuntimeKind } from '../types';
import { RUNTIME_CAPABILITIES } from '../capabilities';
import { PlatformCapabilityUnsupportedError } from '../errors';
import type { PlatformDevice, PlatformShellInfo } from './types';
import { createElectronDevice } from '../electron/shell.electron';

/**
 * PlatformDevice (PLAN §5.3).
 *
 * The capability answers (registration type, permission prompts, OTA, version
 * policy, diagnostics, battery, Wi-Fi) are the desktop-correctness surface;
 * the three shell methods are the seam the Electron main process fills.
 *
 * `openExternal` on mobile/web keeps today's behaviour: the in-app browser
 * choice is a capability (`inAppBrowser`) read by `open-in-app-page.ts`, and
 * this method is the plain fallback both branches already end up using.
 */

const ALLOWED_EXTERNAL_SCHEMES = new Set(['http:', 'https:', 'mailto:', 'tel:']);

function assertAllowedExternalUrl(url: string): void {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    throw new Error(`Refusing to open a malformed external URL: ${url}`);
  }
  if (!ALLOWED_EXTERNAL_SCHEMES.has(parsed.protocol)) {
    throw new Error(`Refusing to open external URL with scheme '${parsed.protocol}'.`);
  }
}

function capabilityFields(kind: RuntimeKind) {
  const capabilities = RUNTIME_CAPABILITIES[kind];
  return {
    registrationType: capabilities.deviceRegistrationType,
    platformTag: kind,
    osPermissionPrompts: capabilities.osPermissionPrompts,
    otaUpdates: capabilities.otaUpdates,
    remoteVersionPolicy: capabilities.remoteVersionPolicy,
    deviceDiagnostics: capabilities.deviceDiagnostics,
    batteryTelemetry: capabilities.batteryTelemetry,
    wifiManagement: capabilities.wifiManagement,
  } as const;
}

function createBrowserDevice(kind: Exclude<RuntimeKind, 'electron'>): PlatformDevice {
  return {
    ...capabilityFields(kind),
    getShellInfo: async (): Promise<PlatformShellInfo> => ({
      runtime: kind,
      appVersion: CONFIG.VITE_APP_VERSION,
    }),
    openExternal: async (url: string): Promise<void> => {
      assertAllowedExternalUrl(url);
      window.open(url, '_blank', 'noopener,noreferrer');
    },
    // Nothing outside the renderer holds app data on mobile/web;
    // `reset-app-data.ts` already covers everything that exists.
    clearShellAppData: async (): Promise<void> => undefined,
    // A mobile OS owns its app lifecycle and a browser tab cannot close
    // itself: fail closed rather than pretend the app quit.
    quitApp: async (): Promise<void> => {
      throw new PlatformCapabilityUnsupportedError('appExit', kind);
    },
    answerCloseRequest: async (): Promise<void> => {
      throw new PlatformCapabilityUnsupportedError('appExit', kind);
    },
    onCloseRequested: () => () => undefined,
    // PointCraft Home exists only beside the Windows desktop shell; there is
    // no file to keep anywhere else.
    publishSessionFile: async () => {
      throw new PlatformCapabilityUnsupportedError('posSessionFile', kind);
    },
  };
}

export function createPlatformDevice(kind: RuntimeKind): PlatformDevice {
  // T3.A: the shell surface is implemented. `createElectronDevice` routes the
  // three shell methods through `window.pointcraftElectron.shell`, and still
  // fails closed with `ElectronBridgeUnavailableError` when no shell is
  // present — which is what a vitest run, or a `dist-pos` opened in a plain
  // browser tab, correctly is.
  if (kind === 'electron') return createElectronDevice();
  return createBrowserDevice(kind);
}
